import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {BusinessECommerceStore, EcommerceDashboardPeriod, RefundState, WebBusinessService} from 'web-angular-client';
import {DropdownItem} from '../../../sdk/interface/data/DropdownItem';
import {FilterPersistenceService} from '../../../sdk/component/search/filter-persistence.service';
import {RefundSearchService} from '../../../sdk/component/search/refund-search.service';
import {SearchFilterBase} from '../../../sdk/component/search/search-filter-base';
import {SearchService} from '../../../sdk/component/search/search.service';
import {AlertService} from '../../../sdk/service/alert.service';
import {ECOMMERCE_TRANSACTIONS_PERIOD_OPTIONS} from '../../ecommerce/ecommerce-period-options';
import {getRefundStatusLabel} from '../../../sdk/interface/refundStatusUtil';

/**
 * Tabbed filter for the refund transactions list — same look and interaction model as
 * {@code PaymentSearchComponent}, but scoped to the fields the refund search backend supports: period, date-created
 * range, amount range, and refund status. The period preset is forwarded via {@link RefundSearchService.currentPeriod}
 * (resolved server-side); the remaining fields travel as the search criteria.
 *
 * <p>
 * Unlike {@code PaymentSearchComponent}, amount conversion (major → minor) is left to {@link RefundSearchService} —
 * this component passes the raw rand amounts so they are converted exactly once.
 */
@Component({
    selector: 'app-refund-search',
    templateUrl: './refund-search.component.html',
    styleUrls: ['./refund-search.component.scss'],
    standalone: false
})
export class RefundSearchComponent extends SearchFilterBase implements OnInit, OnDestroy
{
    readonly PERIOD_FILTER_ID = 'periodFilter';
    readonly DATE_CREATED_FILTER_ID = 'dateCreatedFilter';
    readonly ACCOUNT_NUMBER_FILTER_ID = 'accountNumberFilter';
    readonly REFERENCE_FILTER_ID = 'referenceNumberFilter';
    readonly NAME_FILTER_ID = 'nameFilter';
    readonly AMOUNT_FILTER_ID = 'amountFilter';
    readonly STATUS_FILTER_ID = 'statusFilter';
    readonly SHOP_FILTER_ID = 'ecommerceStoreFilter';

    /** Sentinel for the "any state" option (no state filter). */
    readonly ANY_STATUS = '';

    readonly periodOptions: DropdownItem[] = ECOMMERCE_TRANSACTIONS_PERIOD_OPTIONS;
    readonly statusOptions: DropdownItem[] = [
        {label: 'Any Status', value: this.ANY_STATUS},
        ...Object.values(RefundState).map(state => ({label: getRefundStatusLabel(state), value: state}))
    ];

    ecommerceStoreOptions: DropdownItem[] = [];

    form: UntypedFormGroup;
    selectedPeriod: EcommerceDashboardPeriod | null = EcommerceDashboardPeriod.LAST_30_D;

    constructor(
        protected searchDataService: SearchService,
        protected filterPersistenceService: FilterPersistenceService,
        protected route: ActivatedRoute,
        protected formBuilder: UntypedFormBuilder,
        protected ngxLoader: NgxUiLoaderService,
        private refundSearchService: RefundSearchService,
        private webBusinessService: WebBusinessService,
        private alertService: AlertService)
    {
        super(searchDataService, ngxLoader, filterPersistenceService, route);
    }

    ngOnInit(): void
    {
        this.form = this.formBuilder.group({
            dateTimeCreatedLower: [null],
            dateTimeCreatedUpper: [null],
            accountNumber: [null],
            referenceNumber: [null],
            customer: [null],
            amountLower: [null],
            amountUpper: [null],
            refundState: [this.ANY_STATUS],
            ecommerceStoreIds: ['']
        });
        this._activeTab = this.PERIOD_FILTER_ID;
        this.refundSearchService.currentPeriod = this.selectedPeriod;
        this.loadEcommerceStores();
        this.initializeSearchPage();
    }

    /**
     * Loads the merchant's ecommerce-store links for the Shop filter. The dropdown value is the
     * business-ecommerce-store (link-row) id, which the backend resolves against the refund's account. Mirrors the
     * ecommerce transactions list. On failure the options stay empty so the filter still offers "All shops", and the
     * merchant is told why the Shop filter is empty (matching the ecommerce payment dashboard).
     */
    private loadEcommerceStores(): void
    {
        this.webBusinessService.getECommerceStoresForBusiness().subscribe({
            next: (stores: BusinessECommerceStore[]) =>
            {
                this.ecommerceStoreOptions = (stores ?? [])
                    .filter(store => store.id != null)
                    .map(store =>
                    {
                        const name = store.eCommerceStoreName ?? `${store.id}`;
                        const label = store.domain ? `${name} – ${store.domain}` : name;
                        return {label, value: store.id};
                    });
            },
            error: () =>
            {
                this.ecommerceStoreOptions = [];
                this.alertService.error('Failed to load e-commerce stores for the filter.');
            }
        });
    }

    ngOnDestroy(): void
    {
        clearInterval(this.interval);
        // Root-scoped service: clear the period so it doesn't leak into the next consumer.
        this.refundSearchService.currentPeriod = null;
    }

    /**
     * Builds the criteria sent to the backend from the current form values. Amounts stay in major units (rand) —
     * {@link RefundSearchService} converts them to minor. A blank date or the "any" status contributes no filter.
     */
    getSearchCriteria(): any
    {
        const value = this.form?.value ?? {};
        const state = value.refundState;
        return {
            dateTimeCreatedLower: this.toIsoStartOfDay(value.dateTimeCreatedLower),
            dateTimeCreatedUpper: this.toIsoEndOfDay(value.dateTimeCreatedUpper),
            accountNumber: this.blankToUndefined(value.accountNumber),
            referenceNumber: this.blankToUndefined(value.referenceNumber),
            customer: this.blankToUndefined(value.customer),
            amountLower: value.amountLower ?? undefined,
            amountUpper: value.amountUpper ?? undefined,
            refundStates: state ? [state] : undefined,
            businessECommerceStoreIds: this.selectedStoreIds(value.ecommerceStoreIds)
        };
    }

    setFilters(): void
    {
        // No persisted-filter rehydration for v1; the form's defaults are the starting state.
    }

    override refresh(): void
    {
        // A manual date range takes precedence over the period preset (the backend would otherwise let the period
        // override the date bounds), so drop the period whenever a range is set — and reflect that in the UI.
        if (this.hasManualDateRange())
        {
            this.selectedPeriod = null;
        }
        this.refundSearchService.currentPeriod = this.selectedPeriod;
        super.refresh();
    }

    private hasManualDateRange(): boolean
    {
        const value = this.form?.value ?? {};
        return !!(value.dateTimeCreatedLower || value.dateTimeCreatedUpper);
    }

    getTabContent(event: any): void
    {
        this._activeTab = event.target.id.toString();
    }

    onPeriodChange(period: EcommerceDashboardPeriod | null): void
    {
        if (!period)
        {
            return;
        }
        // Period and a manual date range both filter on creation date and the backend lets the period override the
        // bounds, so they're mutually exclusive: choosing a period clears any manual range.
        this.selectedPeriod = period;
        this.form.get('dateTimeCreatedLower')?.reset(null);
        this.form.get('dateTimeCreatedUpper')?.reset(null);
        this.search();
    }

    get activeTab(): string
    {
        return this._activeTab;
    }

    get tabActive(): boolean
    {
        return !!this._activeTab;
    }

    clear(activeTab: string): void
    {
        switch (activeTab)
        {
            case this.DATE_CREATED_FILTER_ID:
                this.form.get('dateTimeCreatedLower')?.reset(null);
                this.form.get('dateTimeCreatedUpper')?.reset(null);
                break;
            case this.ACCOUNT_NUMBER_FILTER_ID:
                this.form.get('accountNumber')?.reset(null);
                break;
            case this.REFERENCE_FILTER_ID:
                this.form.get('referenceNumber')?.reset(null);
                break;
            case this.NAME_FILTER_ID:
                this.form.get('customer')?.reset(null);
                break;
            case this.AMOUNT_FILTER_ID:
                this.form.get('amountLower')?.reset(null);
                this.form.get('amountUpper')?.reset(null);
                break;
            case this.STATUS_FILTER_ID:
                this.form.get('refundState')?.reset(this.ANY_STATUS);
                break;
            case this.SHOP_FILTER_ID:
                this.form.get('ecommerceStoreIds')?.reset('');
                break;
            case this.PERIOD_FILTER_ID:
                this.selectedPeriod = null;
                break;
        }
        this.search();
    }

    clearAll(): void
    {
        this.selectedPeriod = null;
        this.form.reset({refundState: this.ANY_STATUS, ecommerceStoreIds: ''});
        this.search();
    }

    /**
     * Lower bound for the date-created range. The picked calendar date is interpreted as local wall-clock time (no 'Z'
     * suffix) before being serialised to UTC, so the boundary lines up with the local day the merchant sees in the
     * results table rather than midnight UTC (which, in SAST, would exclude the first two hours of the chosen day).
     */
    private toIsoStartOfDay(date: string | null | undefined): string | undefined
    {
        return date ? new Date(`${date}T00:00:00`).toISOString() : undefined;
    }

    /** Upper bound for the date-created range; local end-of-day (see {@link toIsoStartOfDay} for the rationale). */
    private toIsoEndOfDay(date: string | null | undefined): string | undefined
    {
        return date ? new Date(`${date}T23:59:59.999`).toISOString() : undefined;
    }

    /** Trims a text filter, returning {@code undefined} for a blank value so it applies no constraint. */
    private blankToUndefined(value: string | null | undefined): string | undefined
    {
        const trimmed = (value ?? '').trim();
        return trimmed.length ? trimmed : undefined;
    }

    /**
     * Maps the selected shop (a business-ecommerce-store id, or '' for all shops) to the single-element id list the
     * backend expects, or {@code undefined} when no shop is chosen.
     */
    private selectedStoreIds(raw: string | null | undefined): number[] | undefined
    {
        if (raw == null || raw === '')
        {
            return undefined;
        }
        const id = Number(raw);
        return Number.isNaN(id) ? undefined : [id];
    }
}
